import type { ClienteApi, ConVersion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components as Analitica } from '../tipos/analitica';
import type { components as Disponibilidad } from '../tipos/disponibilidad';
import type { components as Pases } from '../tipos/pases-acceso';
import type { components, operations } from '../tipos/reservas-sala';

/**
 * Operaciones del módulo Reservas que usa el panel (8.1 «Reservas del día» y 8.4 «Por completar»), con su `operationId` y
 * los tipos generados de `reservas-sala.yaml`, más lo que esas pantallas leen de otros módulos: el horario del día
 * (`getEffectiveHours`, Disponibilidad, G245), la llegada desde el panel (`listPasses`, `validatePass` y `undoArrival`,
 * Pases y Acceso, D-047) y las cifras del Socio (`queryIndicator`, Analítica, G465).
 *
 * - `listReservations` sustituye a la de `ops/pagos.ts` (va después en `crearOperaciones`): admite todos los filtros del
 *   contrato y devuelve `counts` por estado cuando se pide un `service_date` (G797). El panel no cuenta reservas.
 * - Lo que avisa al cliente, mueve dinero o registra una llegada lleva `Idempotency-Key`.
 * - Nombres de los clientes con `listCustomersByIds` y zonas y recursos con `listZones` / `listResources` (ya en
 *   `ops/pagos.ts`).
 */
type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

export type Reserva = Esquema<'Reservation'>;
export type EstadoReserva = Esquema<'ReservationStatus'>;
export type ListaReservas = Esquema<'ReservationList'>;
export type CifrasReservas = Esquema<'ReservationCounts'>;
export type CanalReserva = Esquema<'Channel'>;
/** `apply_charge` tiene valor por defecto (`true`): se puede no mandar. */
export type PeticionCompletar = Omit<Esquema<'CompletionRequest'>, 'apply_charge'> & { apply_charge?: boolean };
export type PeticionNoShow = Partial<Esquema<'NoShowRequest'>>;
export type Pase = Pases['schemas']['Pass'];
export type Lectura = Pases['schemas']['ScanResult'];
export type PeticionLectura = Pases['schemas']['ValidationRequest'];
export type HorarioEfectivo = Disponibilidad['schemas']['EffectiveHours'];
export type CifrasIndicador = Analitica['schemas']['IndicatorValues'];
type ConsultaReservasApi = NonNullable<operations['listReservations']['parameters']['query']>;

/** Filtros de listReservations (los del contrato, con su página). */
export type FiltrosReservas = Pagina & Omit<ConsultaReservasApi, 'cursor' | 'limit'>;

const r = (businessId: string, resto: string): `/v1/${string}` => `${negocio(businessId)}${resto}`;
const id = (valor: string) => encodeURIComponent(valor);

export function operacionesReservas(api: ClienteApi) {
    return {
        // ---- Reservas (8.1, 8.4) ----
        /**
         * listReservations (`reservas.reservas.ver`, «lo suyo» para el RRPP): por jornada (`service_date`), estado, zona,
         * evento, cliente o localizador. Con `service_date`, `counts` por estado (G797) para las cifras de arriba.
         */
        listReservations: (businessId: string, filtros: FiltrosReservas = {}) =>
            api.pedir<ListaReservas>('GET', r(businessId, `/reservations${consulta({ ...filtros })}`)),
        /** getReservation con su `ETag`. */
        getReservation: (businessId: string, reservationId: string): Promise<ConVersion<Reserva>> =>
            api.pedirConVersion<Reserva>('GET', r(businessId, `/reservations/${id(reservationId)}`)),
        /**
         * completePendingReservation (`reservas.pendientes.completar`, G14): `attended: true` la completa (sin cargo);
         * `false` es un no-show marcado por el personal (G15), con `apply_charge` si la política lo prevé.
         */
        completePendingReservation: (businessId: string, reservationId: string, datos: PeticionCompletar, clave = claveIdempotencia()) =>
            api.pedir<Reserva>('POST', r(businessId, `/reservations/${id(reservationId)}/completion`), { cuerpo: datos, idempotencia: clave }),
        /** markReservationNoShow (`reservas.no_show.marcar`): desde la hora de inicio más el margen (G322); antes, `409`. */
        markReservationNoShow: (businessId: string, reservationId: string, datos: PeticionNoShow = {}, clave = claveIdempotencia()) =>
            api.pedir<Reserva>('POST', r(businessId, `/reservations/${id(reservationId)}/no-show`), { cuerpo: datos, idempotencia: clave }),
        /**
         * undoReservationNoShow (`reservas.no_show.deshacer`, con motivo): vuelve a `confirmed` o, si su franja ya terminó,
         * a `pending_completion` (G792). El panel pinta el estado que devuelve.
         */
        undoReservationNoShow: (businessId: string, reservationId: string, motivo: string, clave = claveIdempotencia()) =>
            api.pedir<Reserva>('POST', r(businessId, `/reservations/${id(reservationId)}/no-show/undo`), { cuerpo: { reason: motivo }, idempotencia: clave }),

        // ---- Pases y Acceso: «Ha llegado» desde el panel (D-047) ----
        // ---- Disponibilidad: el horario del día (G245) ----
        // ---- Analítica: lo que ve el Socio (G465) ----
    };
}
